import React from 'react';
import MarkdownIt from 'markdown-it';
import type Token from 'markdown-it/lib/token.mjs';

import { Icon } from 'components/icons/Icon';
import { CardTitle } from 'components/ui/card';
import { safeExternalLink } from './linkifyText';
import { getQuestionCustomTextSizePx, installQuestionTextSizes, QUESTION_MARKDOWN_MAX_NESTING } from './questionMarkdownSizes';
import {
  ANSWER_REFERENCE_PATTERN, AnswerReferenceContext, answerReferencePlainText, trimAnswerReferenceFallback,
  type AnswerReferenceContextValue,
} from './answerReferences';

export { QUESTION_TEXT_SIZES } from './questionMarkdownSizes';

// Parse to tokens, then construct React elements. Never evaluate authored HTML.
// markdown-it supports maxNesting at runtime; its public Options type omits it.
const markdownOptions: MarkdownIt.Options & { maxNesting: number } = {
  html: false, breaks: true, linkify: false, typographer: false,
  maxNesting: QUESTION_MARKDOWN_MAX_NESTING,
};
const markdown = new MarkdownIt(markdownOptions);
installQuestionTextSizes(markdown);
// REQ-106: {{question_key}} or {{question_key|fallback}} shows the learner's own answer.
markdown.inline.ruler.before('escape', 'answer_reference', (state, silent) => {
  const match = ANSWER_REFERENCE_PATTERN.exec(state.src.slice(state.pos));
  if (!match) return false;
  if (!silent) {
    const token = state.push('answer_reference', '', 0);
    token.content = match[0];
    token.meta = { key: match[1], fallback: match[2] === undefined ? null : trimAnswerReferenceFallback(match[2]) };
  }
  state.pos += match[0].length;
  return true;
});
markdown.inline.ruler.before('escape', 'question_reference', (state, silent) => {
  const match = /^\{(\d+|orangeCheckmark)\}/.exec(state.src.slice(state.pos));
  if (!match) return false;
  if (!silent) {
    const token = state.push(match[1] === 'orangeCheckmark' ? 'question_icon' : 'question_citation', '', 0);
    token.content = match[1];
  }
  state.pos += match[0].length;
  return true;
});

export const safeQuestionLink = safeExternalLink;

export const extractAnswerReferenceKeys = (segments: Array<string | null | undefined>): string[] => {
  const keys: string[] = [];
  const visit = (tokens: Token[]) => tokens.forEach((token) => {
    if (token.type === 'answer_reference' && !keys.includes(token.meta.key)) keys.push(token.meta.key);
    if (token.children && token.type !== 'image') visit(token.children);
  });
  segments.forEach((segment) => { if (segment && segment.includes('{{')) visit(markdown.parse(segment, {})); });
  return keys;
};

export const extractQuestionCitationIndices = (
  segments: Array<string | null | undefined>,
): number[] => {
  const indices = new Set<number>();
  const visit = (tokens: Token[]) => tokens.forEach((token) => {
    if (token.type === 'question_citation' && Number(token.content) > 0 && Number.isFinite(Number(token.content))) indices.add(Number(token.content));
    if (token.children && token.type !== 'image') visit(token.children);
  });
  segments.forEach((segment) => { if (segment) visit(markdown.parse(segment, {})); });
  return Array.from(indices).sort((a, b) => a - b);
};

interface QuestionTextProps {
  text?: string | null;
  /** Compact labels keep inline emphasis while flattening block structures. */
  inline?: boolean;
  citations?: string[];
  className?: string;
  /** Keep paragraph boundaries when rendering a prompt inside its heading. */
  paragraphBreaks?: boolean;
  /** Disable nested links in interactive answer labels. */
  links?: boolean;
  /** Header, text and subtext resolve REQ-106 answer references; other fields keep `{{…}}` literal. */
  answerReferences?: boolean;
}

const BLOCK_CLASSES: Record<string, string> = {
  p: 'my-3 first:mt-0 last:mb-0',
  h1: 'mt-6 mb-3 text-heading question-markdown-heading question-heading-1 first:mt-0',
  h2: 'mt-5 mb-3 text-heading question-markdown-heading question-heading-2 first:mt-0',
  h3: 'mt-4 mb-2 text-heading question-markdown-heading question-heading-3 first:mt-0',
  h4: 'mt-4 mb-2 text-heading question-markdown-heading question-heading-4 first:mt-0',
  h5: 'mt-4 mb-2 text-heading question-markdown-heading question-heading-5 first:mt-0',
  h6: 'mt-4 mb-2 text-heading question-markdown-heading question-heading-6 first:mt-0',
  ul: 'my-3 list-disc pl-6 space-y-1',
  ol: 'my-3 list-decimal pl-6 space-y-1',
  blockquote: 'my-4 border-l-4 border-primary/40 pl-4 text-muted-foreground',
  hr: 'my-5 border-border',
  table: 'w-full border-collapse text-left',
  th: 'border border-border px-3 py-2 font-semibold bg-muted/50',
  td: 'border border-border px-3 py-2 align-top',
  pre: 'my-3 overflow-x-auto rounded bg-muted p-3 whitespace-pre-wrap',
  code: 'rounded bg-muted px-1 font-mono text-[0.9em]',
};

const renderTokens = (
  tokens: Token[], options: Required<Pick<QuestionTextProps, 'inline' | 'citations' | 'links' | 'paragraphBreaks'>> & {
    /** Reveal faces are noninteractive, including while hidden. */
    reveal?: boolean;
    answerReferences?: AnswerReferenceContextValue | null;
  },
): React.ReactNode[] => {
  let cursor = 0;
  const read = (): React.ReactNode[] => {
    const nodes: React.ReactNode[] = [];
    while (cursor < tokens.length) {
      const token = tokens[cursor++];
      if (token.nesting === -1) break;
      const key = `token-${cursor}`;
      if (token.type === 'inline') {
        nodes.push(<React.Fragment key={key}>{renderTokens(token.children ?? [], options)}</React.Fragment>);
        continue;
      }
      if (token.type === 'text') { nodes.push(token.content); continue; }
      if (token.type === 'answer_reference') {
        if (options.answerReferences === undefined) { nodes.push(token.content); continue; }
        nodes.push(<AnswerReference key={key} referenceKey={token.meta.key} fallback={token.meta.fallback}
          context={options.reveal ? null : options.answerReferences ?? null} />);
        continue;
      }
      if (token.type === 'question_citation') {
        if (options.reveal) { nodes.push(`{${token.content}}`); continue; }
        const index = Number(token.content);
        const citation = options.citations[index - 1];
        nodes.push(<sup key={key} className="ml-0.5 align-super text-xs font-semibold text-primary"
          title={citation || undefined} aria-label={`Citation ${index}${citation ? `: ${citation}` : ''}`}>{index}</sup>);
        continue;
      }
      if (token.type === 'question_icon') {
        nodes.push(<Icon key={key} name="orangeCheckmark2" size={18} className="mx-1 inline-block align-middle" />);
        continue;
      }
      if (token.type === 'softbreak' || token.type === 'hardbreak') {
        nodes.push(<br key={key} />); continue;
      }
      if (token.type === 'code_inline') { nodes.push(<code key={key} className={BLOCK_CLASSES.code}>{token.content}</code>); continue; }
      if (token.type === 'fence' || token.type === 'code_block') {
        nodes.push(options.inline ? <code key={key}>{token.content}</code> : <pre key={key} className={BLOCK_CLASSES.pre}><code>{token.content}</code></pre>);
        continue;
      }
      if (token.type === 'image') {
        // Images are managed by the question's existing media fields, never loaded from Markdown.
        nodes.push(<React.Fragment key={key}>{`![${token.content}](${token.attrGet('src') ?? ''})`}</React.Fragment>);
        continue;
      }
      const children = token.nesting === 1 ? read() : undefined;
      if (token.type === 'question_size_inline_open' || token.type === 'question_size_block_open') {
        const tag = token.block && !options.inline ? 'div' : 'span';
        const sizeClass = token.attrGet('class') ?? '';
        const pixels = getQuestionCustomTextSizePx(sizeClass.slice('text-'.length));
        nodes.push(React.createElement(tag, {
          key, className: `question-text-size ${sizeClass}`,
          // The parser allowlist supplies the number; authored CSS never reaches styles.
          ...(pixels === undefined ? {} : { style: { fontSize: `${pixels / 16}rem` } }),
        }, children));
        continue;
      }
      if (token.hidden) { nodes.push(<React.Fragment key={key}>{children}</React.Fragment>); continue; }
      if (token.tag === 'a') {
        const href = options.links ? safeQuestionLink(token.attrGet('href')) : undefined;
        nodes.push(href ? <a key={key} href={href} target="_blank" rel="noopener noreferrer" className="underline decoration-primary/60 underline-offset-2 text-primary break-words" title={token.attrGet('title') ?? undefined}>{children}</a> : <React.Fragment key={key}>{children}</React.Fragment>);
        continue;
      }
      if (options.inline && token.block) {
        if (options.paragraphBreaks && token.tag === 'p') {
          if (nodes.length) nodes.push(<React.Fragment key={`${key}-break`}><br /><br /></React.Fragment>);
          nodes.push(<React.Fragment key={key}>{children}</React.Fragment>);
        } else {
          nodes.push(<React.Fragment key={key}>{children}{' '}</React.Fragment>);
        }
        continue;
      }
      const allowed = ['p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li', 'blockquote', 'hr', 'table', 'thead', 'tbody', 'tr', 'th', 'td', 'strong', 'em', 's'];
      if (!allowed.includes(token.tag)) { nodes.push(<React.Fragment key={key}>{children ?? token.content}</React.Fragment>); continue; }
      const align = token.attrGet('style')?.replace('text-align:', '');
      const element = React.createElement(token.tag, {
        key,
        className: options.reveal && ['ul', 'ol'].includes(token.tag)
          ? `${BLOCK_CLASSES[token.tag]} text-left`
          : BLOCK_CLASSES[token.tag],
        ...(token.tag === 'ol' && token.attrGet('start') ? { start: Number(token.attrGet('start')) } : {}),
        ...(['th', 'td'].includes(token.tag) && ['left', 'center', 'right'].includes(align ?? '') ? { style: { textAlign: align } } : {}),
        ...(token.tag === 'th' ? { scope: 'col' } : {}),
      }, children);
      nodes.push(token.tag === 'table'
        ? <div key={key} className={options.reveal ? 'my-4 max-w-full [&_td]:break-words [&_th]:break-words [&_table]:table-fixed' : 'my-4 max-w-full overflow-x-auto'}
          {...(options.reveal ? {} : { tabIndex: 0, role: 'region', 'aria-label': 'Table' })}>{element}</div>
        : element);
    }
    return nodes;
  };
  return read();
};

/**
 * A learner's own earlier answer, rendered as plain text and never as Markdown.
 * Missing answers use the authored fallback; authoring previews show the key.
 */
const AnswerReference: React.FC<{
  referenceKey: string;
  fallback: string | null;
  context: AnswerReferenceContextValue | null;
}> = ({ referenceKey, fallback, context }) => {
  if (context?.mode === 'preview') {
    return (
      <span className="question-answer-reference question-answer-reference--preview" data-testid="answer-reference-preview"
        title={fallback === null ? `Learner's answer to ${referenceKey}` : `Learner's answer to ${referenceKey}. If unanswered: ${fallback || '(nothing)'}`}>
        {`answer: ${referenceKey}`}
      </span>
    );
  }
  const value = context?.references[referenceKey];
  if (value?.status === 'answered' && value.text) {
    return <span className="question-answer-reference" data-testid="answer-reference">{value.text}</span>;
  }
  if (fallback !== null) return <>{fallback}</>;
  return (
    <span className="question-answer-reference question-answer-reference--missing" data-testid="answer-reference-missing">
      {answerReferencePlainText(referenceKey, null, value ? context : null)}
    </span>
  );
};

/** The same renderer is used by learner cards and the author preview. */
export const QuestionText: React.FC<QuestionTextProps> = ({
  text, inline = false, citations = [], className, links = true, paragraphBreaks = false, answerReferences: resolvesReferences = false,
}) => {
  const answerReferences = React.useContext(AnswerReferenceContext);
  if (text == null || text === '') return null;
  const content = renderTokens(markdown.parse(text, {}), {
    inline, citations, links, paragraphBreaks, answerReferences: resolvesReferences ? answerReferences : undefined,
  });
  return inline
    ? <span className={className}>{content}</span>
    : <div className={['question-markdown min-w-0 break-words', className].filter(Boolean).join(' ')}>{content}</div>;
};

/** Shared reveal-face preview/runtime: inherit card typography and never add nested controls. */
export const RevealCardText: React.FC<{
  text?: string | null;
  face: 'front' | 'back';
  className?: string;
}> = ({ text, face, className }) => {
  if (text == null || text === '') return null;
  const tokens = markdown.parse(text, {});
  const isPlain = (items: Token[]): boolean => items.every((token) =>
    ['paragraph_open', 'paragraph_close', 'inline', 'text', 'softbreak', 'question_citation'].includes(token.type)
    && (!token.children || isPlain(token.children)));
  // Preserve all existing blank lines and spaces in ordinary authored card text.
  const plain = isPlain(tokens) && !/\\[!-/:-@[-`{-~]/.test(text);
  const content = plain ? text : renderTokens(tokens, {
    inline: face === 'front', citations: [], links: false, paragraphBreaks: true, reveal: true,
  });
  return React.createElement(face === 'front' ? 'span' : 'div', {
    className: ['min-w-0 break-words', plain ? 'whitespace-pre-wrap' : 'whitespace-normal', className].filter(Boolean).join(' '),
  }, content);
};

/** Plain paragraphs retain the established question heading; structural Markdown owns its headings. */
export const QuestionPrompt: React.FC<Pick<QuestionTextProps, 'text' | 'citations' | 'className'>> = ({ text, citations, className }) => {
  if (!text) return null;
  const hasStructuralBlocks = markdown.parse(text, {}).some((token) =>
    !['paragraph_open', 'paragraph_close', 'inline'].includes(token.type));
  return hasStructuralBlocks
    ? <QuestionText text={text} citations={citations} answerReferences className={['question-prompt', className].filter(Boolean).join(' ')} />
    : <CardTitle className={['question-prompt pb-0', className].filter(Boolean).join(' ')} title={<QuestionText text={text} citations={citations} inline paragraphBreaks answerReferences />} />;
};

/** Plain readable labels; spoken Voice targets are canonicalized on the backend. */
export const questionTextToPlainText = (
  text?: string | null,
  { literalCitations = false, answerReferences }: {
    literalCitations?: boolean;
    /** Pass the active context (or null) only for header, text and subtext. */
    answerReferences?: AnswerReferenceContextValue | null;
  } = {},
): string => {
  if (!text) return '';
  const inlineText = (tokens: Token[]): string => tokens.map((token) => {
    if (token.type === 'answer_reference') {
      return answerReferences === undefined ? token.content : answerReferencePlainText(token.meta.key, token.meta.fallback, answerReferences);
    }
    if (token.type === 'question_citation') return literalCitations ? `{${token.content}}` : '';
    if (token.type === 'question_icon') return '';
    if (token.type === 's_open') return 'Deleted: ';
    if (token.type === 's_close') return '. ';
    if (token.type === 'softbreak' || token.type === 'hardbreak') return ' ';
    if (token.type === 'image') return token.content;
    return token.children ? inlineText(token.children) : token.content;
  }).join('');
  const tokens = markdown.parse(text, {});
  const chunks: string[] = [];
  let headers: string[] = [];
  let row: string[] = [];
  let inTable = false;
  let inHeader = false;
  let hasBodyRows = false;
  tokens.forEach((token) => {
    if (token.type === 'table_open') { inTable = true; headers = []; hasBodyRows = false; }
    else if (token.type === 'table_close') {
      if (!hasBodyRows && headers.some((header) => header.trim())) chunks.push(headers.join('; ') + '.');
      inTable = false;
    }
    else if (token.type === 'thead_open') inHeader = true;
    else if (token.type === 'thead_close') inHeader = false;
    else if (token.type === 'tr_open') row = [];
    else if (token.type === 'tr_close') {
      if (inHeader) headers = row;
      else {
        hasBodyRows = true;
        chunks.push(row.map((cell, index) => headers[index] ? `${headers[index]}: ${cell}` : cell).join('; ') + '.');
      }
    } else if (token.type === 'inline') {
      const value = inlineText(token.children ?? []);
      if (inTable) row.push(value); else chunks.push(value);
    } else if (token.type === 'fence' || token.type === 'code_block') chunks.push(token.content);
  });
  return chunks.join('\n').replace(/[ \t]+/g, ' ').trim();
};
